import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  FileDown, Loader2, Lock, Info, ArrowUpRight, Crown, Sparkles, HeartHandshake, Check, Compass, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PathwayCard } from "@/components/PathwayCard";
import { getReport, pdfUrl, createUpgrade, tier2Interest, logEvent, runMatch, getMatchConfig } from "@/lib/api";
import { NOT_ADVICE, ZERO_COMMISSION } from "@/components/Disclaimers";

const TAB_DEFS = [
  { key: "all", label: "All" },
  { key: "descent", label: "Descent" },
  { key: "marriage", label: "Marriage" },
  { key: "naturalization", label: "Naturalization" },
  { key: "investment", label: "Investment" },
  { key: "residence", label: "Residence" },
];

const curSym = (c) => (c === "eur" ? "€" : c === "usd" ? "$" : "");

/**
 * LifestyleLens — collapsible re-rank widget on the report page.
 *
 * Pattern: the visitor already has a paid report showing pathways ranked by
 * the eligibility engine's accessibility score. This widget lets them
 * OPTIONALLY overlay their lifestyle preferences on top — same countries,
 * different sort order. When active, PathwayCards in every tab are re-sorted
 * by fit_score. A pill above the pathway list makes the active state obvious
 * so the visitor never forgets they're seeing a re-ordered view.
 *
 * Deliberately in-page (not a redirect to /match) — the friction of leaving
 * the report would kill adoption. The quiz here is a lightweight 3-question
 * form that reuses the same API + scoring as /match.
 */
const LifestyleLens = ({ reportId, fitActive, onApply }) => {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [climate, setClimate] = useState(3);
  const [household, setHousehold] = useState("solo");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && !config) {
      getMatchConfig()
        .then((c) => {
          setConfig(c);
          setRanking(c.factors.map((f) => f.key));
        })
        .catch(() => toast.error("Could not load lifestyle matcher"));
    }
  }, [open, config]);

  const apply = async () => {
    setBusy(true);
    try {
      const res = await runMatch({
        ranking,
        climate_pref: ranking.indexOf("climate") <= 2 ? climate : null,
        household,
        limit: 25,
        report_id: reportId,
      });
      const byCountry = {};
      (res.shortlist || []).forEach((s) => { byCountry[s.country] = s.fit_score; });
      onApply(byCountry);
      logEvent("report_lifestyle_lens_applied", reportId, {
        top_factor: ranking[0], household, coverage: res.count,
      });
      setOpen(false);
    } catch {
      toast.error("Could not compute lifestyle fit. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    onApply(null);
    logEvent("report_lifestyle_lens_cleared", reportId, {});
  };

  const move = (idx, delta) => {
    const next = [...ranking];
    const [item] = next.splice(idx, 1);
    next.splice(idx + delta, 0, item);
    setRanking(next);
  };

  return (
    <Card
      data-testid="report-lifestyle-lens"
      className="mt-8 p-4 bg-white border-[hsl(var(--atlas-teal))]/30"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Compass className="h-4 w-4 text-[hsl(var(--atlas-teal))]" />
          <span className="font-medium text-[hsl(var(--atlas-navy))]">
            Sort by lifestyle fit
          </span>
          {fitActive && (
            <Badge
              className="bg-[hsl(var(--atlas-teal))] text-white border-0"
              data-testid="report-lifestyle-active-badge"
            >
              Active
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {fitActive && (
            <Button
              variant="outline"
              size="sm"
              onClick={clear}
              data-testid="report-lifestyle-clear"
            >
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          )}
          <Button
            variant={open ? "outline" : "default"}
            size="sm"
            onClick={() => setOpen(!open)}
            className={open ? "" : "bg-[hsl(var(--atlas-navy))] text-white"}
            data-testid="report-lifestyle-toggle"
          >
            {open ? "Close" : fitActive ? "Change preferences" : "Set preferences"}
          </Button>
        </div>
      </div>
      {open && config && (
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-2">
              Rank what matters most
            </div>
            <div className="space-y-1.5">
              {ranking.map((key, i) => {
                const item = config.factors.find((f) => f.key === key);
                return (
                  <div key={key} className="flex items-center gap-2 rounded-lg border px-2 py-1.5 text-sm">
                    <span className="text-muted-foreground w-4 tabular-nums">{i + 1}.</span>
                    <span className="flex-1 text-[hsl(var(--atlas-navy))]">{item.label}</span>
                    <button
                      type="button"
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                      className="text-xs px-1.5 py-0.5 border rounded disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      disabled={i === ranking.length - 1}
                      onClick={() => move(i, 1)}
                      className="text-xs px-1.5 py-0.5 border rounded disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Household</div>
              <div className="grid grid-cols-3 gap-2">
                {config.households.map((h) => (
                  <button
                    key={h.key}
                    type="button"
                    onClick={() => setHousehold(h.key)}
                    className={[
                      "rounded-lg border px-2 py-2 text-xs transition-colors",
                      household === h.key
                        ? "border-[hsl(var(--atlas-navy))] bg-[hsl(var(--atlas-navy))] text-white"
                        : "hover:bg-[hsl(var(--muted))] text-[hsl(var(--atlas-navy))]",
                    ].join(" ")}
                  >
                    {h.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Climate preference: <span className="text-[hsl(var(--atlas-navy))]">
                  {["Cold","Cool","Temperate","Warm","Hot"][climate-1]}
                </span>
              </div>
              <input
                type="range"
                min={1}
                max={5}
                step={1}
                value={climate}
                onChange={(e) => setClimate(parseInt(e.target.value, 10))}
                className="w-full"
              />
            </div>
            <Button
              onClick={apply}
              disabled={busy}
              className="w-full bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white"
              data-testid="report-lifestyle-apply"
            >
              {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Applying…</> : "Re-sort by fit"}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

const LockedFullMobilityCard = ({ locked, pricing, onUpgrade, upgrading }) => {
  const c = curSym(pricing?.currency || "eur");
  const upgradeAmt = pricing?.upgrade?.amount ?? 80;
  const upgradePreAmt = pricing?.upgrade?.pre_promo_amount;
  const promo = pricing?.promo;
  const promoActive = !!(promo && promo.active && upgradePreAmt && upgradePreAmt !== upgradeAmt);
  const rows = [
    ["naturalization", "Fast-track naturalization routes", locked?.naturalization ?? 0],
    ["residence", "Residence permits (Plan-B bases)", locked?.residence ?? 0],
    ["investment", "Investment (CBI) options (shown neutrally)", locked?.investment ?? 0],
  ];
  return (
    <Card
      data-testid="report-locked-full-mobility"
      className="relative overflow-hidden p-6 bg-white border-[hsl(var(--atlas-navy))]/20"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[hsl(var(--atlas-navy))] flex items-center justify-center">
              <Lock className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold text-[hsl(var(--atlas-navy))]">
                Full Mobility Report · Locked
              </div>
              <div className="text-xs text-muted-foreground">
                Your Citizenship Report is unlocked. Add residence & investment routes any time.
              </div>
            </div>
          </div>

          <ul className="mt-4 space-y-2 text-sm">
            {rows.map(([k, label, n]) => (
              <li key={k} className="flex items-center justify-between gap-3">
                <span className="text-[hsl(var(--atlas-slate))] flex items-center gap-2">
                  <Check className="h-4 w-4 text-[hsl(var(--atlas-teal))]" /> {label}
                </span>
                <Badge variant="outline" className="tabular-nums">
                  {n} {n === 1 ? "country" : "countries"}
                </Badge>
              </li>
            ))}
          </ul>
          <div className="mt-3 text-xs text-muted-foreground">
            {locked?.total ?? 0} pathways across {locked?.countries ?? 0} additional countries.
          </div>
        </div>

        <div className="min-w-[220px]">
          <div className="rounded-xl border bg-[hsl(var(--muted))] p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">One-click upgrade</div>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              <span className="font-display text-3xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
                {c}{upgradeAmt}
              </span>
              {promoActive && (
                <span
                  className="text-sm text-muted-foreground line-through tabular-nums"
                  data-testid="report-upgrade-strike"
                >
                  {c}{upgradePreAmt}
                </span>
              )}
              <span className="text-xs text-muted-foreground">one-time</span>
            </div>
            {promoActive && (
              <div
                data-testid="report-upgrade-promo-badge"
                className="mt-1 text-[11px] font-medium text-[hsl(var(--atlas-teal))]"
              >
                {promo.label || "Launch promo"} applied
              </div>
            )}
            <Button
              onClick={onUpgrade}
              disabled={upgrading}
              data-testid="report-upgrade-button"
              className="mt-3 w-full bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white"
            >
              {upgrading
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
                : <><Crown className="h-4 w-4 mr-2" /> Upgrade to Full Mobility <ArrowUpRight className="h-4 w-4 ml-1" /></>}
            </Button>
            <p className="mt-2 text-[11px] text-muted-foreground text-center">Secure checkout via Stripe · Test mode</p>
          </div>
        </div>
      </div>
    </Card>
  );
};

const Tier2InterestCard = ({ reportId, email, hasCbiContext }) => {
  const [state, setState] = useState("idle"); // idle | loading | done

  const register = async () => {
    setState("loading");
    try {
      await tier2Interest({
        report_id: reportId,
        email: email || "anonymous@atlas.local",
        cbi_context: !!hasCbiContext,
      });
      setState("done");
      toast.success("Thanks. We'll be in touch about our personalized advisory tier.");
    } catch (e) {
      setState("idle");
      toast.error("Could not register interest. Please try again.");
    }
  };

  return (
    <Card data-testid="report-tier2-card" className="p-6 bg-white">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center shrink-0">
          <HeartHandshake className="h-5 w-5 text-[hsl(var(--atlas-navy))]" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold text-[hsl(var(--atlas-navy))]">
            Want a hand implementing this?
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            We&apos;re building a personalized, still commission-free advisory tier: direct help preparing documents, engaging vetted independent counsel, and staging your move. No commitment. One click to register your interest.
          </p>
          <div className="mt-4">
            {state === "done" ? (
              <Badge
                data-testid="report-tier2-registered"
                className="bg-emerald-50 text-emerald-800 border border-emerald-200"
              >
                <Check className="h-3 w-3 mr-1" /> Interest registered. We&apos;ll be in touch.
              </Badge>
            ) : (
              <Button
                onClick={register}
                disabled={state === "loading"}
                data-testid="report-tier2-button"
                variant="outline"
                className="border-[hsl(var(--atlas-navy))]/25 text-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--accent))]"
              >
                {state === "loading"
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Registering…</>
                  : <><Sparkles className="h-4 w-4 mr-2" /> I&apos;d be interested</>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [upgrading, setUpgrading] = useState(false);
  // Lifestyle-fit lens: when populated, pathway lists in the grouped view are
  // re-sorted so countries with higher fit_score appear first. Kept OUTSIDE
  // the report payload — the visitor opts in by taking the /match quiz from
  // this page. Storing in local state (not URL) keeps the report bookmark stable.
  const [fitByCountry, setFitByCountry] = useState(null);

  useEffect(() => {
    getReport(id).then((d) => {
      setData(d);
      if (d.paid) logEvent("report_viewed", id, { tier: d.tier_purchased });
    }).catch(() => toast.error("Report not found")).finally(() => setLoading(false));
  }, [id]);

  const grouped = useMemo(() => {
    const g = { all: [], descent: [], marriage: [], naturalization: [], investment: [], residence: [] };
    (data?.pathways || []).forEach((p) => {
      g.all.push(p);
      (g[p.category] = g[p.category] || []).push(p);
    });
    // When the lifestyle-fit lens is active, re-sort every category by
    // fit_score DESC (falling back to engine score for pathways whose country
    // is outside our 50 lifestyle-covered set). Non-mutating: rebuild arrays.
    if (fitByCountry) {
      const scoreOf = (p) => (
        (fitByCountry[p.country] != null ? fitByCountry[p.country] : -1)
      );
      Object.keys(g).forEach((k) => {
        g[k] = [...g[k]].sort((a, b) => scoreOf(b) - scoreOf(a));
      });
    }
    return g;
  }, [data, fitByCountry]);

  const hasCbi = useMemo(
    () => (data?.pathways || []).some((p) => p.category === "investment"),
    [data],
  );

  const doUpgrade = async () => {
    setUpgrading(true);
    try {
      const origin = window.location.origin;
      const res = await createUpgrade({ report_id: id, origin_url: origin });
      if (res.url) window.location.href = res.url;
      else throw new Error("no url");
    } catch (e) {
      toast.error("Could not start upgrade. Please try again.");
      setUpgrading(false);
    }
  };

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--atlas-navy))]" /></div>;
  if (!data) return <div className="py-24 text-center text-muted-foreground">Report not found.</div>;

  if (!data.paid) {
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-[hsl(var(--atlas-navy))] flex items-center justify-center"><Lock className="h-5 w-5 text-white" /></div>
        <h1 className="mt-4 font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))]">This report is locked</h1>
        <p className="mt-2 text-muted-foreground">Complete your one-time purchase to unlock the full report.</p>
        <Button className="mt-5 bg-[hsl(var(--atlas-navy))] text-white" onClick={() => navigate(`/teaser?report_id=${id}`)}>Unlock report</Button>
      </div>
    );
  }

  const ps = data.profile_summary || {};
  const gen = data.generated_at ? new Date(data.generated_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";
  const tier = data.tier_purchased;
  const isBaseTier = tier === "citizenship";
  const tierLabel = tier === "full" ? "Full Mobility Report" : "Citizenship Report";

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
      {/* Report header */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="rounded-2xl border bg-white p-6 relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 atlas-divider" />
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge variant="secondary">{tierLabel}</Badge>
                <Badge
                  data-testid="report-tier-badge"
                  className={
                    tier === "full"
                      ? "bg-[hsl(var(--atlas-navy))] text-white border-0"
                      : "bg-[hsl(var(--accent))] text-[hsl(var(--atlas-navy))] border-0"
                  }
                >
                  {tier === "full" ? <><Crown className="h-3 w-3 mr-1" /> Full tier</> : "Base tier"}
                </Badge>
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-semibold text-[hsl(var(--atlas-navy))]">
                {ps.name ? `Prepared for ${ps.name}` : "Your eligibility report"}
              </h1>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {ps.nationalities?.map((n) => <Badge key={n} variant="outline">{n}</Badge>)}
                {ps.birth_country && <Badge variant="outline">Born: {ps.birth_country}</Badge>}
                {ps.marital_status && <Badge variant="outline" className="capitalize">{ps.marital_status}{ps.spouse_nationality ? ` · ${ps.spouse_nationality}` : ""}</Badge>}
              </div>
              <div className="mt-2 text-xs text-muted-foreground">Generated {gen}</div>
            </div>
            <a href={pdfUrl(id)} target="_blank" rel="noreferrer" data-testid="report-download-pdf-button">
              <Button className="bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white"><FileDown className="mr-2 h-4 w-4" /> Download PDF</Button>
            </a>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Descent", data.teaser.descent_claims], ["Fast-track natz.", data.teaser.fast_track_naturalization], ["Residence", data.teaser.accessible_residence], ["Investment", data.teaser.investment_options]].map(([l, n]) => (
              <div key={l} className="rounded-lg bg-[hsl(var(--muted))] px-3 py-2 text-center">
                <div className="font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">{n}</div>
                <div className="text-[11px] text-muted-foreground">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Locked full-mobility section for base-tier buyers */}
      {isBaseTier && data.locked_summary && (
        <div className="mt-6">
          <LockedFullMobilityCard
            locked={data.locked_summary}
            pricing={data.pricing}
            onUpgrade={doUpgrade}
            upgrading={upgrading}
          />
        </div>
      )}

      {/* Lifestyle-fit lens */}
      <LifestyleLens
        reportId={id}
        fitActive={!!fitByCountry}
        onApply={setFitByCountry}
      />

      {/* Pathways */}
      <div className="mt-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            {TAB_DEFS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`report-tab-${t.key}`} className="data-[state=active]:bg-[hsl(var(--atlas-navy))] data-[state=active]:text-white">
                {t.label} <span className="ml-1 opacity-70 tabular-nums">{grouped[t.key]?.length || 0}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {TAB_DEFS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-5">
              {grouped[t.key]?.length ? (
                <div className="grid gap-4">{grouped[t.key].map((p, i) => <PathwayCard key={`${p.country}-${p.category}-${i}`} p={p} index={i} />)}</div>
              ) : (
                <div className="text-center text-muted-foreground py-12">
                  {isBaseTier && ["naturalization", "residence", "investment"].includes(t.key)
                    ? "These routes are part of the Full Mobility Report. Upgrade above to unlock."
                    : "No pathways in this category for your profile."}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Honesty section */}
      {data.not_eligible?.length > 0 && (
        <div className="mt-10" data-testid="report-not-eligible-section">
          <h2 className="font-display text-xl font-semibold text-[hsl(var(--atlas-navy))]">Pathways you do NOT currently qualify for, and why not</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-3">An honesty section no commissioned competitor will publish.</p>
          <Accordion type="single" collapsible className="rounded-xl border bg-white divide-y">
            {data.not_eligible.map((n, i) => (
              // `n.country` is unique within a report (one entry per country); using
              // it as the key lets React preserve accordion open-state correctly
              // if the list is ever re-sorted or filtered client-side.
              <AccordionItem key={n.country || `ne-${i}`} value={`ne-${n.country || i}`} className="border-0 px-4">
                <AccordionTrigger className="hover:no-underline"><span className="font-medium text-[hsl(var(--atlas-navy))]">{n.country}</span></AccordionTrigger>
                <AccordionContent><p className="text-sm text-[hsl(var(--atlas-slate))]">{n.reason}</p><p className="text-sm text-muted-foreground mt-2">{n.how_to_qualify}</p></AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}

      {/* Tier-2 interest capture */}
      <div className="mt-8">
        <Tier2InterestCard reportId={id} email={ps.email} hasCbiContext={hasCbi} />
      </div>

      <div className="mt-10 grid sm:grid-cols-2 gap-3">
        <Card className="p-4 bg-white flex items-start gap-2"><Info className="h-4 w-4 mt-0.5 text-[hsl(var(--atlas-steel))]" /><p className="text-xs text-[hsl(var(--atlas-slate))]">{NOT_ADVICE}</p></Card>
        <Card className="p-4 bg-white flex items-start gap-2"><Info className="h-4 w-4 mt-0.5 text-[hsl(var(--success))]" /><p className="text-xs text-[hsl(var(--atlas-slate))]">{ZERO_COMMISSION}</p></Card>
      </div>
    </div>
  );
}
